import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { canSee } from "../../utils/sectionPermissions";
import { CircularProgress } from "@material-ui/core";
import { TextField, Select, MenuItem, Box } from "@mui/material";
import { ReactComponent as NoApps } from "../../img/svg/NoApps.svg";
import { ReactComponent as NoAccess } from "../../img/svg/NoAccess.svg";
import "./MyApps.css";
import UserTable from "./MyAppsTable";
import ExportSubscriptionsDialog from "./ExportSubscriptionsDialog";

const MyApps = (props) => {
  const [myApps, setMyApps] = useState([]);
  const navigate = useNavigate();
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [isSomethingChanged, setIsSomethingChanged] = React.useState(false);
  // Search + filters + selected workspace are all URL-controlled so the
  // browser Back button restores state (open a workspace → back → the
  // list still has the same query/filters, not a blank one).
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get("q") || "";
  const paymentStatus = searchParams.get("paid") || "";
  const overdueStatus = searchParams.get("overdue") || "";
  const paidStatus = searchParams.get("method") || "";
  const providerFilter = searchParams.get("provider") || "";
  const billingFilter = searchParams.get("billing") || "";

  // Replace history when only filter values change — avoids polluting Back
  // history with every keystroke while still making the current URL shareable.
  const patchParams = (patch) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) {
          if (v === "" || v == null) next.delete(k);
          else next.set(k, v);
        }
        return next;
      },
      { replace: true },
    );
  };
  // Surfaces a failed /all_workspace instead of rendering an empty table that
  // looks like "no workspaces exist".
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    setIsLoaded(false);
    setLoadError("");
    async function fetctAppData() {
      try {
        fetch(`${process.env.REACT_APP_SERVER_URL}/all_workspace`, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          method: "GET",
        })
          .then((response) => {
            if (response.status === 200) {
              return response.json();
            } else if (response.status === 202) {
              setIsLoaded(true);
              console.log("token expired");
              navigate("/login", { state: { sessionExpired: true } });
              return;
            } else {
              setIsLoaded(true);
              console.log("some error");
              return response.json();
            }
          })
          .then((data) => {
            // `myApps` is initialised to [] but was then overwritten with
            // whatever the response carried. On any non-200 (and on the 202
            // token-expired branch, which returns nothing at all) `data.data`
            // is undefined, so the `.filter` below threw and took the whole
            // page down with "Cannot read properties of undefined".
            // Keep the state an array ALWAYS and report the failure instead.
            if (Array.isArray(data?.data)) {
              setMyApps(data.data);
            } else {
              setMyApps([]);
              if (data) {
                setLoadError(
                  data.msg || "Could not load workspaces. Check the API logs.",
                );
              }
            }
            setIsLoaded(true);
          })
          .catch((error) => {
            console.log(error);
            setMyApps([]);
            setLoadError("Could not reach the server.");
            setIsLoaded(true);
          });
      } catch (error) {
        console.log(error);
        setMyApps([]);
        setLoadError("Could not load workspaces.");
        setIsLoaded(true);
      }
    }
    fetctAppData();
    // eslint-disable-next-line
  }, [navigate, isSomethingChanged]);

  // A query looks like a "reference id" (needs a backend lookup rather
  // than the local haystack search) when it matches a known prefix or
  // shape: Stripe sub/invoice, or a Zoho-shaped SO number. Pure numbers
  // are NOT considered refs here — those already do a client-side
  // workspace-id lookup, and if that misses the user can hit Enter to
  // escalate to a backend order-id lookup.
  const REF_PREFIX_RE = /^(sub_|in_|pay_|order_|SO-|INV-)/i;
  const looksLikeRef = (s) => REF_PREFIX_RE.test(String(s || "").trim());

  const [refLookupPending, setRefLookupPending] = React.useState(false);
  const [refLookupError, setRefLookupError] = React.useState("");

  const runRefLookup = React.useCallback(
    async (ref) => {
      const trimmed = String(ref || "").trim();
      if (!trimmed) return;
      setRefLookupPending(true);
      setRefLookupError("");
      try {
        const res = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/admin/lookup_by_ref`,
          {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({ ref: trimmed }),
          },
        );
        const json = await res.json().catch(() => ({}));
        if (!json?.success || !json?.data?.workspace_id) {
          setRefLookupError(json?.msg || "No workspace found for this reference");
          return;
        }
        // Jump straight into the workspace detail with a highlight hint
        // for the specific subscription (if we resolved one). Clears the
        // free-text q so re-clicking Back restores the list without the
        // reference id lingering as a filter.
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete("q");
            next.set("workspaceId", String(json.data.workspace_id));
            if (json.data.subscription_id) {
              next.set("highlightSub", String(json.data.subscription_id));
            } else {
              next.delete("highlightSub");
            }
            return next;
          },
          { replace: false },
        );
      } catch (e) {
        setRefLookupError(e?.message || "Lookup failed");
      } finally {
        setRefLookupPending(false);
      }
    },
    [setSearchParams],
  );

  // Auto-trigger the lookup when a ref-shaped value is typed / pasted.
  // Debounced so per-keystroke pastes don't fire 20 requests.
  React.useEffect(() => {
    if (!looksLikeRef(searchQuery)) {
      setRefLookupError("");
      return;
    }
    const t = setTimeout(() => runRefLookup(searchQuery), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Database-id search: "315", "sub:315", "bill:456", "order:789",
  // "ws:34". A bare number can be a workspace, a subscription, a bill
  // and an order all at once, so the backend returns every hit and we
  // list them above the table; a typed prefix narrows it to one table.
  const DB_ID_QUERY_RE =
    /^(?:(sub|ps|subscription|bill|order|ord|ws|workspace)\s*[:#-]?\s*)?\d+$/i;
  const trimmedQuery = String(searchQuery || "").trim();
  const isDbIdQuery = DB_ID_QUERY_RE.test(trimmedQuery);
  const isPrefixedDbIdQuery = isDbIdQuery && !/^\d+$/.test(trimmedQuery);

  // { q, matches } — keyed by the query that produced it so a slow
  // response never decorates a newer query.
  const [dbLookup, setDbLookup] = React.useState({ q: "", matches: [] });
  const [dbLookupPending, setDbLookupPending] = React.useState(false);
  const dbMatches =
    isDbIdQuery && dbLookup.q === trimmedQuery ? dbLookup.matches : [];

  React.useEffect(() => {
    if (!isDbIdQuery) {
      setDbLookupPending(false);
      return;
    }
    let cancelled = false;
    setDbLookupPending(true);
    const t = setTimeout(async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/admin/lookup_db_id`,
          {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({ q: trimmedQuery }),
          },
        );
        const json = await res.json().catch(() => ({}));
        if (cancelled) return;
        setDbLookup({
          q: trimmedQuery,
          matches: Array.isArray(json?.data?.matches) ? json.data.matches : [],
        });
      } catch (_) {
        if (!cancelled) setDbLookup({ q: trimmedQuery, matches: [] });
      } finally {
        if (!cancelled) setDbLookupPending(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmedQuery]);

  // Open a hit: jump into its workspace with the subscription
  // highlighted. Drops q for the same reason runRefLookup does — the
  // detail view resolves the workspace against the filtered list.
  const openDbMatch = (m) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("q");
        next.set("workspaceId", String(m.workspace_id));
        if (m.subscription_id) {
          next.set("highlightSub", String(m.subscription_id));
        } else {
          next.delete("highlightSub");
        }
        return next;
      },
      { replace: false },
    );
  };

  // workspace id → subscription id to highlight when that row is opened.
  const highlightSubByWorkspace = React.useMemo(() => {
    const map = {};
    for (const m of dbMatches) {
      if (m.subscription_id && map[m.workspace_id] == null) {
        map[m.workspace_id] = m.subscription_id;
      }
    }
    return map;
  }, [dbMatches]);

  const handleSearchChange = (event) => {
    patchParams({ q: event.target.value });
  };

  // Enter-to-escalate: when the client-side substring search returns
  // nothing (or the query is a numeric id that didn't match a workspace),
  // hitting Enter fires the backend lookup for order-id / Zoho-number
  // matches even without a distinctive prefix.
  const handleSearchKeyDown = (event) => {
    if (event.key !== "Enter") return;
    const val = String(searchQuery || "").trim();
    if (!val) return;
    if (dbMatches.length === 1) {
      openDbMatch(dbMatches[0]);
      return;
    }
    if (isDbIdQuery) return; // the matches strip is the answer
    runRefLookup(val);
  };

  const handlePaymentStatusChange = (event) => {
    patchParams({ paid: event.target.value });
  };

  const handleOverdueStatusChange = (event) => {
    patchParams({ overdue: event.target.value });
  };

  const handlePaidStatusChange = (event) => {
    patchParams({ method: event.target.value });
  };

  const handleProviderFilterChange = (event) => {
    patchParams({ provider: event.target.value });
  };

  const handleBillingFilterChange = (event) => {
    patchParams({ billing: event.target.value });
  };

  // Union of every distinct provider name across every workspace's subs —
  // this drives the "Filter by provider" dropdown. Deduped
  // CASE-INSENSITIVELY (first spelling seen wins) so "Ingram" and
  // "ingram" collapse to one option; the filter comparison is already
  // case-insensitive so the picker value only ever needs to be one form.
  const providerOptions = React.useMemo(() => {
    const map = new Map(); // key: lowercased, value: first-seen canonical spelling
    for (const w of Array.isArray(myApps) ? myApps : []) {
      for (const p of w?.subscription_providers || []) {
        const trimmed = String(p || "").trim();
        if (!trimmed) continue;
        const key = trimmed.toLowerCase();
        if (!map.has(key)) map.set(key, trimmed);
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase()),
    );
  }, [myApps]);

  // A pure-digit query is treated as an EXACT workspace-id lookup —
  // typing "42" returns only workspace id=42, not workspaces that happen
  // to have "42" in a name/email/product. Non-numeric queries fall
  // through to the existing substring search.
  const numericIdQuery =
    searchQuery && /^\d+$/.test(searchQuery.trim())
      ? Number(searchQuery.trim())
      : null;

  // If the query is a pure number that EXACTLY matches some workspace's
  // id, short-circuit the whole list to that one row and skip chip
  // filters. If no workspace matches the id (e.g. the admin typed a
  // phone number that happens to be all digits), fall through so the
  // phone matcher below still has a chance to find it.
  const anyWorkspaceIdMatch =
    numericIdQuery !== null &&
    (Array.isArray(myApps) ? myApps : []).some(
      (w) => Number(w?.id) === numericIdQuery,
    );

  // Defensive: even if a future code path sets non-array state, the page must
  // degrade to an empty table rather than crash on render.
  const filteredAppsData = (Array.isArray(myApps) ? myApps : []).filter((key) => {
    if (numericIdQuery !== null && anyWorkspaceIdMatch) {
      // Short-circuit: exact id match, ignore all other filters so an
      // id lookup can find hidden/overdue/whatever without the admin
      // having to reset the chips first.
      return Number(key?.id) === numericIdQuery;
    }
    // Coerce every field to string before toLowerCase — owner_email /
    // firstName / lastName / company can be null on some rows (deleted
    // Users, workspaces with no company) and .toLowerCase() on null
    // throws and crashes the whole table mid-filter.
    const q = searchQuery.toLowerCase();
    const haystackParts = [
      key?.name,
      key?.company,
      key?.owner_email,
      key?.phone, // workspace-level phone
    ];
    for (const u of key?.workspace_users || []) {
      haystackParts.push(u?.firstName, u?.lastName, u?.email, u?.phone);
    }
    for (const n of key?.subscription_names || []) haystackParts.push(n);
    // Provider names go into the free-text search too, so typing "Ingram"
    // surfaces every workspace with an Ingram-provisioned subscription.
    for (const p of key?.subscription_providers || []) haystackParts.push(p);
    // Domains attached to any sub via required_feilds[].value where
    // .name === "domain" — typing "topfinanceus.com" surfaces every
    // workspace that holds a sub for that domain.
    for (const d of key?.subscription_domains || []) haystackParts.push(d);
    const haystack = haystackParts
      .map((v) => String(v || "").toLowerCase())
      .join(" ");
    // Phone fallback — normalise the query and every phone-shaped field
    // to digits-only so "8591336805" matches "+91 8591336805",
    // "(91) 859-133-6805", etc. Only fires when the query itself carries
    // at least 4 consecutive digits so we don't cross-match arbitrary
    // number fragments in unrelated text.
    const qDigits = String(searchQuery).replace(/\D+/g, "");
    let phoneMatches = false;
    if (qDigits.length >= 4) {
      const phoneHaystack = [
        key?.phone,
        ...(key?.workspace_users || []).map((u) => u?.phone),
      ]
        .map((v) => String(v || "").replace(/\D+/g, ""))
        .filter(Boolean)
        .join(" ");
      phoneMatches = phoneHaystack.includes(qDigits);
    }
    const matchesSearchQuery =
      searchQuery === "" || haystack.includes(q) || phoneMatches;

    const matchesPaymentStatus =
      paymentStatus === "" ||
      (paymentStatus === "paid" && key.paid === true) ||
      (paymentStatus === "unpaid" && key.paid === false);

    // `overdue` also covers cancelled / cancel_next subs that still have an
    // unpaid bill. `status` is a combined string ("overdue | cancel_next"),
    // so an equality check on it missed every workspace with both.
    const isOverdue = key.overdue === true;
    const matchesOverDueStatus =
      overdueStatus === "" ||
      (overdueStatus === "overdue" && isOverdue) ||
      (overdueStatus === "not_overdue" && !isOverdue);

    const matchesPaidStatus =
      paidStatus === "" ||
      (paidStatus === "online" && key.nextOnlinePayment) ||
      (paidStatus === "offline" && !key.nextOnlinePayment);

    // Case-insensitive exact match on provider name — the dropdown shows
    // the canonical spelling from the union set, so equality vs
    // subscription_providers[i] is safe.
    const matchesProvider =
      providerFilter === "" ||
      (key?.subscription_providers || []).some(
        (p) => String(p || "").toLowerCase() === providerFilter.toLowerCase(),
      );

    // A workspace paying for both a monthly and a yearly sub matches both.
    const matchesBilling =
      billingFilter === "" ||
      (key?.subscription_intervals || []).includes(billingFilter);

    return (
      matchesSearchQuery &&
      matchesPaymentStatus &&
      matchesOverDueStatus &&
      matchesPaidStatus &&
      matchesProvider &&
      matchesBilling
    );
  });

  // Bring DB-id hits to the top: the workspace whose own id matches
  // first, then the workspaces of matching subs / bills / orders. A
  // typed prefix ("bill:456") shows only its hits; a bare number with
  // no hit falls through to the normal search (it may be a phone).
  let visibleApps = filteredAppsData;
  if (isDbIdQuery) {
    const orderedIds = [];
    if (anyWorkspaceIdMatch && !isPrefixedDbIdQuery) {
      orderedIds.push(numericIdQuery);
    }
    for (const m of dbMatches) orderedIds.push(Number(m.workspace_id));
    const uniqueIds = Array.from(new Set(orderedIds));
    if (uniqueIds.length || isPrefixedDbIdQuery) {
      const byId = new Map(
        (Array.isArray(myApps) ? myApps : []).map((w) => [Number(w?.id), w]),
      );
      visibleApps = uniqueIds.map((id) => byId.get(id)).filter(Boolean);
    }
  }
  const highlightedWorkspaceIds = new Set(
    dbMatches.map((m) => Number(m.workspace_id)),
  );

  // Export: field picker → .xlsx of every subscription of the workspaces
  // currently in view, grouped by legal name.
  const [exportOpen, setExportOpen] = useState(false);

  const DB_KIND_LABEL = {
    subscription: "Subscription",
    bill: "Bill",
    order: "Order",
    workspace: "Workspace",
  };

  return (
    <>
      {canSee(props.userRole, "myapps") && (
        <>
          <div className="my-apps-container">
            {isLoaded && loadError && (
              <div
                style={{
                  margin: "12px 0",
                  padding: "10px 14px",
                  borderRadius: 8,
                  border: "1px solid #f5c2c7",
                  background: "#fff1f3",
                  color: "#842029",
                  fontSize: 14,
                }}
              >
                {loadError}
              </div>
            )}
            {isLoaded && (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "left",
                    alignItems: "center",
                    gap: "20px",
                  }}
                >
                  <TextField
                    id="filled-basic"
                    type="text"
                    placeholder="Search name / email / phone / domain / provider / product, or paste ID (sub_, in_, pay_, order_, SO-, ticket) or DB id (315, sub:315, bill:456, order:789, ws:34)"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKeyDown}
                    sx={{ width: "30%" }}
                    helperText={
                      refLookupPending || (isDbIdQuery && dbLookupPending)
                        ? "Looking up reference…"
                        : refLookupError || ""
                    }
                    FormHelperTextProps={{
                      sx: {
                        color: refLookupError ? "#c0392b" : "#607084",
                        m: 0,
                      },
                    }}
                  />
                  <Select
                    value={paymentStatus}
                    onChange={handlePaymentStatusChange}
                    displayEmpty
                    sx={{ width: "10%" }}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="unpaid">Unpaid</MenuItem>
                  </Select>
                  <Select
                    value={overdueStatus}
                    onChange={handleOverdueStatusChange}
                    displayEmpty
                    sx={{ width: "10%" }}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    <MenuItem value="overdue">Overdue</MenuItem>
                    <MenuItem value="not_overdue">Non Overdue</MenuItem>
                  </Select>
                  <Select
                    value={billingFilter}
                    onChange={handleBillingFilterChange}
                    displayEmpty
                    sx={{ width: "10%" }}
                  >
                    <MenuItem value="">
                      <em>All billing</em>
                    </MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="yearly">Yearly</MenuItem>
                  </Select>
                  <Select
                    value={paidStatus}
                    onChange={handlePaidStatusChange}
                    displayEmpty
                    sx={{ width: "10%" }}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    <MenuItem value="online">Online</MenuItem>
                    <MenuItem value="offline">Offline</MenuItem>
                  </Select>
                  <Select
                    value={providerFilter}
                    onChange={handleProviderFilterChange}
                    displayEmpty
                    sx={{ width: "14%" }}
                  >
                    <MenuItem value="">
                      <em>All providers</em>
                    </MenuItem>
                    {providerOptions.map((name) => (
                      <MenuItem key={name} value={name}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>

                  <h2>Count :{visibleApps.length}</h2>
                  <button
                    type="button"
                    className="add_new"
                    onClick={() => setExportOpen(true)}
                    disabled={visibleApps.length === 0}
                    title="Download every subscription of the workspaces in this view"
                    style={{ padding: "8px 16px", whiteSpace: "nowrap" }}
                  >
                    Export
                  </button>
                  <ExportSubscriptionsDialog
                    open={exportOpen}
                    onClose={() => setExportOpen(false)}
                    workIds={visibleApps.map((w) => w.id)}
                  />
                </div>
                {isDbIdQuery &&
                  !dbLookupPending &&
                  dbLookup.q === trimmedQuery &&
                  // A bare number with no hit is probably a phone —
                  // stay quiet and let the normal search answer it.
                  (dbMatches.length > 0 || isPrefixedDbIdQuery) && (
                  <div className="db-id-matches">
                    {dbMatches.length === 0 ? (
                      <div className="db-id-matches__empty">
                        No subscription, bill, order or workspace with this
                        database id.
                      </div>
                    ) : (
                      dbMatches.map((m) => (
                        <button
                          type="button"
                          key={`${m.kind}-${m.id}`}
                          className={`db-id-match db-id-match--${m.kind}`}
                          onClick={() => openDbMatch(m)}
                          title="Open this workspace with the subscription highlighted"
                        >
                          <span className="db-id-match__kind">
                            {DB_KIND_LABEL[m.kind] || m.kind} #{m.id}
                          </span>
                          <span className="db-id-match__ws">
                            {m.workspace_name || `Workspace #${m.workspace_id}`}
                          </span>
                          {m.title && (
                            <span className="db-id-match__title">{m.title}</span>
                          )}
                          <span className="db-id-match__detail">
                            {[
                              m.detail,
                              m.subscription_id && m.kind !== "subscription"
                                ? `sub #${m.subscription_id}`
                                : null,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
            {!isLoaded && (
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Box>
            )}
            {isLoaded && (
              <>
                {canSee(props.userRole, "myapps") && (
                  <Box>
                    {myApps.length !== 0 && (
                      <div className="app">
                        <UserTable
                          Apps={myApps && visibleApps}
                          highlightedWorkspaceIds={highlightedWorkspaceIds}
                          highlightSubByWorkspace={highlightSubByWorkspace}
                          isSomethingChanged={isSomethingChanged}
                          setIsSomethingChanged={setIsSomethingChanged}
                          role={props.role}
                        />
                      </div>
                    )}
                    {myApps.length === 0 && (
                      <div className="app">
                        <Box
                          sx={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            flexDirection: "column",
                            marginTop: "-4%",
                          }}
                        >
                          <NoApps style={{ height: "50vh" }}></NoApps>
                          <h2 style={{ margin: 0 }}>No Softwares Added</h2>
                          <p style={{ marginTop: 0, fontSize: "12px" }}>
                            Purchase Your First Software
                          </p>
                          <button
                            className="add_new"
                            style={{ width: "15%", height: "7vh" }}
                            onClick={() => navigate("/dashboard/marketplace")}
                          >
                            Purchase
                          </button>
                        </Box>
                      </div>
                    )}
                  </Box>
                )}
              </>
            )}
          </div>
        </>
      )}
      {!canSee(props.userRole, "myapps") && (
          <Box>
            <div>
              <center>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                  }}
                >
                  <NoAccess style={{ height: "50vh" }}></NoAccess>
                </Box>
              </center>
            </div>
          </Box>
        )}
    </>
  );
};

export default MyApps;
